import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Clock } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "src/components/ui/button";
import QuestionCard from "../../../../components/Quiz/question-card";
import { BaseUrlApi, ErrorMessage } from "../../../../lib/api";
import { QuizData } from "../../../../lib/languages";
import { useLanguage } from "../../../../context/LanguageContext";
import { useBlockAllCopy } from "../../../../lib/utils";

function parseDuration(value) {
  if (typeof value === "string" && value.includes(":")) {
    const parts = value.split(":").map(Number);
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2]; // HH:MM:SS
    if (parts.length === 2) return parts[0] * 60 + parts[1]; // MM:SS
  }
  const num = Number(value);
  return isNaN(num) ? 0 : num;
}

export default function QuizTaker({ questions, quiz }) {
  const { language } = useLanguage();
  const t = QuizData[language] || QuizData.en;

  const navigate = useNavigate();
  const [navigateIdx, setNavigateIdx] = useState(0);
  const [submitted, setSubmitted] = useState(
    quiz?.show_answer && quiz?.has_attempted
  );
  const [isShow, setIsShow] = useState(submitted);
  const [selectAnswers, setSelectAnswers] = useState({});
  const [allAnswers, setAllAnswers] = useState(
    questions.length > 0
      ? [{ question_id: questions[0].id, option_id: null }]
      : []
  );

  const [timeLeft, setTimeLeft] = useState(() => parseDuration(quiz.duration));
  const intervalRef = useRef(null);
  const durationKey = `quiz_${quiz.id}_duration`;
  const endTimeKey = `quiz_${quiz.id}_endTime`;

  useEffect(() => {
    if (!quiz || submitted) return;

    const totalSecs = parseDuration(quiz.duration);
    const storedDuration = Number(localStorage.getItem(durationKey));
    let storedEndTime = Number(localStorage.getItem(endTimeKey));
    let endTime;

    if (!storedEndTime || storedDuration !== totalSecs) {
      endTime = Date.now() + totalSecs * 1000;
      localStorage.setItem(durationKey, totalSecs.toString());
      localStorage.setItem(endTimeKey, endTime.toString());
    } else {
      endTime = storedEndTime;
    }

    const tick = () => {
      const diff = endTime - Date.now();
      const secs = Math.max(Math.ceil(diff / 1000), 0);
      setTimeLeft(secs);
      if (diff <= 0) {
        clearInterval(intervalRef.current);
        localStorage.removeItem(endTimeKey);
        localStorage.removeItem(durationKey);
        handleAutoSubmit();
      }
    };

    tick();
    intervalRef.current = setInterval(tick, 1000);
    return () => clearInterval(intervalRef.current);
  }, [quiz.id, quiz.duration, submitted]);

  const handleAutoSubmit = () => {
    if (!submitted) submitQuiz();
  };

  const handleAnswerChange = (questionId, optionId) => {
    setSelectAnswers((prev) => ({ ...prev, [questionId]: optionId }));
    setAllAnswers((prev) => {
      const filtered = prev.filter((a) => a.question_id !== questionId);
      return [...filtered, { question_id: questionId, option_id: optionId }];
    });
  };

  const submitQuiz = async () => {
    try {
      await axios.post(`${BaseUrlApi}/student/answer-quiz/${quiz.id}`, {
        answers: allAnswers,
      });
      setSubmitted(true);
      navigate(-1, { replace: true });
    } catch (error) {
      toast("Error", { description: ErrorMessage(error) });
    } finally {
      setIsShow(true);
      localStorage.removeItem(endTimeKey);
      localStorage.removeItem(durationKey);
    }
  };

  const selectedOption = questions[navigateIdx]?.options.find(
    (opt) => opt.student_select
  );

  const formatTime = (secs) => {
    const m = String(Math.floor(secs / 60)).padStart(2, "0");
    const s = String(secs % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  useBlockAllCopy();

  return (
    <div
      className="space-y-6 max-w-4xl mx-auto nocopy"
      dir={language === "ar" ? "rtl" : "ltr"}
    >
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">{t.quizTitle}</h1>
        <div
          className={`flex items-center gap-2 text-lg font-medium ${
            timeLeft < 60
              ? "text-red-600"
              : timeLeft < 180
              ? "text-amber-600"
              : "text-primary"
          }`}
        >
          <Clock className="h-5 w-5" />
          <span>{formatTime(timeLeft)}</span>
        </div>
      </div>

      {questions.length > 0 && (
        <QuestionCard
          question={questions[navigateIdx]}
          updateQuestion={(value) => {
            setAllAnswers((prevAnswers) => {
              const updatedAnswers = prevAnswers.filter(
                (ans) => ans.question_id !== value.id
              );

              if (!value.answer || value.answer.trim() === "") {
                return updatedAnswers;
              }

              return [
                ...updatedAnswers,
                {
                  question_id: value.id,
                  answer_text: value.answer,
                },
              ];
            });
          }}
          removeQuestion={() => {}}
          isEditMode={false}
          isShowMode={isShow}
          selectedAnswer={
            isShow || submitted
              ? selectedOption?.id
              : selectAnswers[questions[navigateIdx].id]
          }
          onAnswerSelect={(optionId) =>
            handleAnswerChange(questions[navigateIdx].id, optionId)
          }
          showCorrectAnswers={!!submitted}
        />
      )}

      <div className="flex items-center justify-between mt-8">
        <Button
          onClick={() => setNavigateIdx((idx) => idx - 1)}
          variant="outline"
          className="w-32"
          disabled={navigateIdx === 0}
        >
          {t.previous}
        </Button>
        {navigateIdx + 1} / {questions.length}
        {navigateIdx < questions.length - 1 &&
        Object.keys(selectAnswers).length !== questions.length ? (
          <Button
            onClick={() => setNavigateIdx((idx) => idx + 1)}
            className="w-32"
          >
            {t.next}
          </Button>
        ) : (
          <Button
            onClick={submitQuiz}
            className="w-32 bg-blue-600 hover:bg-blue-700"
            disabled={
              Object.keys(allAnswers).length !== questions.length || submitted
            }
          >
            {t.submitQuiz}
          </Button>
        )}
      </div>

      {submitted && (
        <div className="p-4 bg-green-500/20 border border-green-200 rounded-md text-center">
          <h3 className="text-lg font-medium">{t.submittedSuccessTitle}</h3>
          <p className="text-green-600 flex items-center justify-center">
            <Check className="h-5 w-5 mr-2" />
            {t.submittedSuccessMessage}
          </p>
        </div>
      )}
    </div>
  );
}
